import * as THREE from "three";
import { VFX } from "../../../../art/vfx.js";
import { PostFx } from "./postfx";
import { reducedVfx } from "../ui/settings";
import { appliedTier } from "./graphicsPrefs";
import { glContexts } from "../infra/glContexts";
import { loadVfxAtlas } from "./vfxAtlas";

/**
 * The chest stage's own transparent WebGL canvas (chestStage.ts): a second context costing no more than the game's —
 * the tier's pixel ratio and MSAA, no canvas antialiasing (it only receives the post chain's one full-screen quad, or the
 * plain render without post) — its bloom, the VFX library once its atlas is in, and the frame loop. The loop disposes
 * everything when the canvas leaves the page (or never reaches it). `tick(real s)` runs the stage each frame before the
 * render; `onDispose` lets the stage drop its scene. Presentation only.
 */
export class StageCanvas {
  readonly canvas = document.createElement("canvas");
  readonly renderer: THREE.WebGLRenderer;
  readonly post: PostFx | null = null;
  vfx: VFX | null = null;
  private raf = 0;
  private last = performance.now();
  private wasConnected = false;
  private readonly born = performance.now();
  private readonly closeContext: () => void;
  private disposed = false;
  /**
   * A stage whose canvas has not reached the page this long after it was made is given up. An end screen torn down
   * before the stage's first frame (a quick tap while frames are slow) was never seen connected, so the stage kept
   * rendering off-page — holding its WebGL context and ~7 MB — for good (RC1 soak).
   */
  static readonly OrphanMs = 2000;

  /** The stage's pixel ratio: the main renderer's effective tier's, capped by the device's own. */
  static pixelRatio(): number { return Math.min(window.devicePixelRatio || 1, appliedTier().pixelRatio); }

  constructor(readonly scene: THREE.Scene, readonly camera: THREE.PerspectiveCamera, width: number, height: number,
    private readonly tick: (real: number) => void, private readonly onDispose: () => void) {
    this.canvas.className = "chest3d";
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: false, alpha: true, premultipliedAlpha: true });
    this.closeContext = glContexts.open("chest");
    this.renderer.setPixelRatio(StageCanvas.pixelRatio());
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.size(width, height);
    if (!reducedVfx()) this.post = new PostFx(this.renderer, scene, camera, width, height, 0.75, true, appliedTier().msaa);
    PostFx.applyChestTone(this.renderer, this.post);         // tone + bloom numbers are look's (look.ts CHEST_TONE)
    void this.loadVfx();
    this.raf = requestAnimationFrame(this.frame);
  }

  /** The canvas' CSS size. */
  get width(): number { return parseFloat(this.canvas.style.width) || this.canvas.clientWidth; }
  get height(): number { return parseFloat(this.canvas.style.height) || this.canvas.clientHeight; }

  size(width: number, height: number, pixelRatio?: number): void {
    if (pixelRatio !== undefined) this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(width, height, false);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.post?.resize(width, height);
  }

  private async loadVfx(): Promise<void> {
    if (reducedVfx()) return;
    try {
      const { meta, texture: tex } = await loadVfxAtlas();
      if (this.raf) this.vfx = new VFX(this.scene, this.camera, tex, meta);
    } catch { /* the clips carry the moment on their own */ }
  }

  private frame = (now: number): void => {
    if (this.canvas.isConnected) this.wasConnected = true;
    else if (this.wasConnected || now - this.born > StageCanvas.OrphanMs) { this.dispose(); return; }   // the screen was torn down
    // the next frame first: a throw below must not stop the loop that disposes the stage when its screen goes
    this.raf = requestAnimationFrame(this.frame);
    if (!this.canvas.isConnected) return;                           // not in the page yet: nothing to draw
    // clamped at 0 too: the first frame's timestamp can be earlier than the `performance.now()` the stage was made at,
    // and a negative step froze the drop for good (dropT < 0 is chestShow's "not dropping")
    const real = Math.max(0, Math.min(0.1, (now - this.last) / 1000));
    this.last = now;
    this.tick(real);
    if (this.post) this.post.render(); else this.renderer.render(this.scene, this.camera);
  };

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.onDispose();
    this.post?.dispose();
    this.renderer.dispose();
    // Give the context back NOW rather than at garbage collection: WebKit keeps ~16 live contexts and, past that,
    // drops the OLDEST — the main game canvas — so each chest reveal left behind would bring a black game closer.
    this.renderer.forceContextLoss();
    this.closeContext();
    this.canvas.remove();
  }
}
